<template>
	<v-dialog :value="value" @input="$emit('input', $event)" max-width="800px" scrollable>
		<v-card>
			<v-card-title class="d-flex align-center pa-4">
				<v-icon class="mr-2">mdi-package-variant</v-icon>
				<div>
					<span class="dialog-title">Committed Inventory</span>
					<span class="dialog-subtitle ml-2">{{ styleCode }}<template v-if="sizeName"> — {{ sizeName }}</template></span>
				</div>
				<v-spacer />
				<v-btn icon @click="close">
					<v-icon>mdi-close</v-icon>
				</v-btn>
			</v-card-title>

			<v-divider />

			<v-card-text class="pa-0 dialog-scroll">
				<!-- Loading state -->
				<div v-if="loading" class="d-flex justify-center pa-8">
					<v-progress-circular indeterminate color="primary" />
				</div>

				<!-- Empty state -->
				<div v-else-if="commitments.length === 0" class="text-center pa-8 grey--text empty-state">
					No commitments found for this item.
				</div>

				<!-- Commitments table -->
				<template v-else>
					<!-- Show active toggle -->
					<div v-if="hasActiveCommitments" class="px-4 pt-3 pb-1">
						<v-checkbox
							v-model="showActive"
							label="Show active orders"
							hide-details
							dense
							class="mt-0 pt-0 show-active-toggle"
						></v-checkbox>
					</div>

					<!-- No orphans message -->
					<div v-if="filteredCommitments.length === 0" class="text-center pa-6 grey--text empty-state">
						No orphaned commitments found. Toggle "Show active orders" to see all.
					</div>

					<v-simple-table v-else dense class="commitments-table">
						<thead>
							<tr>
								<th class="select-col">
									<v-checkbox
										v-model="allSelected"
										@change="toggleAll"
										hide-details
										class="mt-0 pt-0"
									></v-checkbox>
								</th>
								<th>Order #</th>
								<th>Style Code</th>
								<th v-if="hasSizes">Size</th>
								<th class="text-right">Qty</th>
								<th class="status-col"></th>
							</tr>
						</thead>
						<tbody>
							<tr
								v-for="c in filteredCommitments"
								:key="`${c.lineItemId}-${c.commitmentId}-${c.isSized}`"
								:class="{ 'orphaned-row': c.isOrphaned }"
							>
								<td class="select-col">
									<v-checkbox
										v-model="selected"
										:value="c"
										hide-details
										class="mt-0 pt-0"
									></v-checkbox>
								</td>
								<td>
									<a
										v-if="c.salesOrderId"
										:href="`/sales-orders/orders/${c.salesOrderId}`"
										target="_blank"
										class="order-link"
										@click.stop
									>
										{{ c.orderNumber }}
										<v-icon x-small class="ml-1 link-icon">mdi-open-in-new</v-icon>
									</a>
									<span v-else>{{ c.orderNumber }}</span>
								</td>
								<td>{{ c.currentStyleCode }}</td>
								<td v-if="hasSizes">{{ c.sizeName || '—' }}</td>
								<td class="text-right">{{ c.quantityCommitted }}</td>
								<td class="status-col">
									<v-chip
										v-if="c.isOrphaned"
										x-small
										color="warning"
										text-color="white"
										title="Orphaned — line item now uses a different style"
									>
										Orphaned
									</v-chip>
									<v-chip
										v-else-if="c.isShipped"
										x-small
										color="grey"
										text-color="white"
										title="Order already shipped — leftover commitment that is safe to remove"
									>
										Shipped
									</v-chip>
								</td>
							</tr>
						</tbody>
						<tfoot>
							<tr class="total-row">
								<td></td>
								<td :colspan="hasSizes ? 3 : 2">Total</td>
								<td class="text-right">{{ filteredTotal }}</td>
								<td></td>
							</tr>
						</tfoot>
					</v-simple-table>

					<!-- Active commitment warning -->
					<v-alert
						v-if="activeSelectedCount > 0"
						type="warning"
						outlined
						dense
						class="mx-4 mt-3 mb-0 active-warning"
					>
						{{ activeSelectedCount }} of the selected commitment{{ activeSelectedCount > 1 ? 's are' : ' is' }}
						from active orders. Removing will un-reserve this inventory.
					</v-alert>
				</template>
			</v-card-text>

			<v-divider v-if="!loading && filteredCommitments.length > 0" />

			<v-card-actions v-if="!loading && filteredCommitments.length > 0" class="pa-4">
				<v-spacer></v-spacer>
				<v-btn text @click="close" :disabled="removing">Cancel</v-btn>
				<v-btn
					color="error"
					:disabled="selected.length === 0"
					:loading="removing"
					@click="showConfirmation = true"
				>
					<v-icon left small>mdi-delete-outline</v-icon>
					Remove Selected ({{ selectedQuantity }})
				</v-btn>
			</v-card-actions>
		</v-card>

		<!-- Confirmation dialog -->
		<v-dialog v-model="showConfirmation" max-width="420" persistent>
			<v-card>
				<v-card-title class="d-flex align-center">
					<v-icon color="error" class="mr-2">mdi-alert</v-icon>
					<span class="dialog-title">Confirm Removal</span>
				</v-card-title>
				<v-card-text class="confirm-text">
					Remove {{ selected.length }} commitment record{{ selected.length > 1 ? 's' : '' }}
					totaling <strong>{{ selectedQuantity }}</strong> units?
					This cannot be undone.
				</v-card-text>
				<v-card-actions class="pa-4">
					<v-spacer></v-spacer>
					<v-btn text @click="showConfirmation = false">Cancel</v-btn>
					<v-btn color="error" @click="removeSelected" :loading="removing">
						<v-icon left small>mdi-delete-outline</v-icon>
						Remove
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-dialog>
</template>

<script>
import { getApiUrl } from "@/helpers";

export default {
	name: "CommitmentDetailsDialog",
	props: {
		value: {
			type: Boolean,
			default: false,
		},
		inventoryItemId: {
			type: Number,
			required: true,
		},
		sizeId: {
			type: Number,
			default: null,
		},
		sizeName: {
			type: String,
			default: "",
		},
		styleCode: {
			type: String,
			default: "",
		},
	},
	data() {
		return {
			commitments: [],
			selected: [],
			loading: false,
			removing: false,
			showConfirmation: false,
			showActive: false,
			allSelected: false,
		};
	},
	computed: {
		hasSizes() {
			return this.commitments.some((c) => c.sizeName);
		},
		hasActiveCommitments() {
			return this.commitments.some((c) => !c.isOrphaned && !c.isShipped);
		},
		filteredCommitments() {
			if (this.showActive) return this.commitments;
			// Shipped-order leftovers are stale reservations, shown by default like orphans
			return this.commitments.filter((c) => c.isOrphaned || c.isShipped);
		},
		filteredTotal() {
			return this.filteredCommitments.reduce((sum, c) => sum + c.quantityCommitted, 0);
		},
		selectedQuantity() {
			return this.selected.reduce((sum, c) => sum + c.quantityCommitted, 0);
		},
		activeSelectedCount() {
			return this.selected.filter((c) => !c.isOrphaned && !c.isShipped).length;
		},
	},
	watch: {
		value: {
			handler(open) {
				if (open) {
					this.fetchCommitments();
				} else {
					this.selected = [];
					this.allSelected = false;
					this.showActive = false;
				}
			},
			immediate: true,
		},
		selected(val) {
			this.allSelected = val.length > 0 && val.length === this.filteredCommitments.length;
		},
		showActive() {
			this.selected = [];
			this.allSelected = false;
		},
	},
	methods: {
		async fetchCommitments() {
			this.loading = true;
			this.commitments = [];
			this.selected = [];
			try {
				let url = getApiUrl(`/api/inventory/bins/${this.inventoryItemId}/commitments`);
				if (this.sizeId != null) {
					url += `?sizeId=${this.sizeId}`;
				}
				const res = await this.$http.get(url);
				this.commitments = res.data;
			} catch (error) {
				console.error("Failed to fetch commitments", error);
				this.$toast.error("Failed to load commitments");
			} finally {
				this.loading = false;
			}
		},
		toggleAll(val) {
			this.selected = val ? [...this.filteredCommitments] : [];
		},
		async removeSelected() {
			this.removing = true;
			try {
				const payload = this.selected.map((c) => ({
					lineItemId: c.lineItemId,
					commitmentId: c.commitmentId,
					isSized: c.isSized,
					isOrphaned: c.isOrphaned,
					quantityCommitted: c.quantityCommitted,
					sizeId: c.sizeId,
					salesOrderId: c.salesOrderId,
				}));
				await this.$http.post(
					getApiUrl(`/api/inventory/bins/${this.inventoryItemId}/commitments/remove`),
					payload
				);
				this.showConfirmation = false;
				this.$emit("removed");
				this.close();
			} catch (error) {
				console.error("Failed to remove commitments", error);
				const message = error?.response?.data?.error || "Failed to remove commitments";
				this.$toast.error(message);
			} finally {
				this.removing = false;
			}
		},
		close() {
			this.$emit("input", false);
		},
	},
};
</script>

<style scoped>
.dialog-scroll {
	max-height: 70vh;
	overflow-y: auto;
}

.dialog-title {
	font-size: 1.6rem;
	font-weight: 500;
}

.dialog-subtitle {
	font-size: 1.3rem;
	font-weight: 400;
	color: #616161;
	border-left: 1px solid #e0e0e0;
	padding-left: 0.8rem;
}

.empty-state {
	font-size: 1.4rem;
}

.confirm-text {
	font-size: 1.4rem !important;
	line-height: 1.6;
}

.show-active-toggle {
	font-size: 1.3rem;
}

.show-active-toggle >>> label {
	font-size: 1.3rem !important;
}

/* Order link */
.order-link {
	color: #1565c0;
	text-decoration: none;
	white-space: nowrap;
}

.order-link:hover {
	text-decoration: underline;
}

.order-link .link-icon {
	opacity: 0.4;
	vertical-align: middle;
}

.order-link:hover .link-icon {
	opacity: 1;
}

/* Table styling */
.commitments-table >>> table {
	border-collapse: collapse;
}

.commitments-table >>> thead tr {
	background-color: #f5f5f5;
}

.commitments-table >>> thead th {
	font-size: 1.3rem !important;
	font-weight: 600 !important;
	text-transform: uppercase;
	color: #424242 !important;
	padding: 12px 16px !important;
	white-space: nowrap;
}

.commitments-table >>> tbody td {
	font-size: 1.4rem !important;
	padding: 10px 16px !important;
}

.commitments-table >>> tfoot td {
	font-size: 1.4rem !important;
	font-weight: 600 !important;
	padding: 12px 16px !important;
	border-top: 2px solid #bdbdbd;
	background-color: #fafafa;
}

.select-col {
	width: 48px !important;
	padding-left: 12px !important;
	padding-right: 4px !important;
}

.status-col {
	width: 90px;
	text-align: center !important;
}

.orphaned-row {
	background-color: #fff8e1;
}

.active-warning {
	font-size: 1.3rem !important;
}

/* Button sizing */
.v-card-actions .v-btn {
	font-size: 1.3rem !important;
}
</style>
